/**
 * De onderdelen van de router die geen scherm zijn: de wortel om alles heen, de omgeving
 * van de routes met alleen een spel (`/:gameId` en `/admin/:gameId`) en de vangnetten.
 */
import type { ReactNode } from 'react';
import { Box, Button, Center, Code, Heading, Text, VStack } from '@chakra-ui/react';
import { Link, Navigate, Outlet, useParams, useRouteError } from 'react-router';
import { Laadscherm } from '@/components/Laadscherm';
import { Melding } from '@/components/Melding';
import { Verbindingsmelding } from '@/components/Verbindingsmelding';
import AdminProviders from '@/screens/Admin/Providers';
import TaskProviders from '@/screens/Tasks/Providers';
import { SpelProvider, useSpel } from '@/spel';

/**
 * Om alle routes: de gedeelde staat van de schermen (in het origineel om de router heen in
 * `index.tsx`) en een vlak van precies de schermhoogte (was `react-div-100vh`).
 */
export function Root() {
  return (
    <TaskProviders>
      <AdminProviders>
        <Box h="100dvh">
          <Outlet />
        </Box>
      </AdminProviders>
    </TaskProviders>
  );
}

/** Zolang het eerste scherm nog laadt. */
export function Opstarten() {
  return (
    <Box h="100dvh">
      <Laadscherm />
    </Box>
  );
}

/**
 * Als een scherm crasht of niet te laden is (bijvoorbeeld een oude chunk na een nieuwe
 * versie van de app). Opnieuw laden lost dat laatste op.
 */
export function Foutscherm() {
  const fout = useRouteError();
  const tekst = fout instanceof Error ? fout.message : String(fout);
  return (
    <Center h="100dvh" p="6">
      <VStack gap="4" textAlign="center" maxW="xl">
        <Heading as="h1" size="xl">
          Er ging iets mis
        </Heading>
        <Code whiteSpace="pre-wrap">{tekst}</Code>
        <Text>Laad de pagina opnieuw. Helpt dat niet, ga dan terug naar het beginscherm.</Text>
        <Button onClick={() => window.location.reload()}>Opnieuw laden</Button>
        <Button asChild variant="outline">
          <Link to="/">Naar het beginscherm</Link>
        </Button>
      </VStack>
    </Center>
  );
}

/**
 * Om `/:gameId` (het gezamenlijke takenscherm) en `/admin/:gameId`: een verbinding met het
 * spel, zonder speler. Bij `/admin/:gameId` staat `spelleider` aan: zonder rechten gaat dat
 * scherm naar het inlogscherm (zie `SpelProvider`).
 */
export function SpelRoute({ spelleider = false }: { spelleider?: boolean }) {
  const { gameId = '' } = useParams();
  return (
    <SpelProvider spelId={gameId} spelleider={spelleider}>
      <OnbekendOfScherm />
      <Verbindingsmelding />
    </SpelProvider>
  );
}

function OnbekendOfScherm() {
  const { status } = useSpel();
  if (status === 'onbekend') {
    return <Melding titel="Dit spel bestaat niet (meer)." tekst="Controleer de spelcode." />;
  }
  return <Outlet />;
}

/**
 * `<Box h="full" padding="10px">` uit `App.tsx` van het origineel. Alleen nog om het scherm
 * van de spelleider; de spelersschermen hebben een eigen rand (`Rolscherm`).
 */
export function Omlijst({ children }: { children?: ReactNode }) {
  return (
    <Box h="full" padding="10px">
      {children ?? <Outlet />}
    </Box>
  );
}

/** Een onbekend pad onder `/:gameId/:userId` gaat naar de fabriek van die speler. */
export function NaarFabriek() {
  const { gameId = '', userId = '' } = useParams();
  return <Navigate to={`/${gameId}/${userId}`} replace />;
}
